import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Alert, 
  Button, 
  CircularProgress, 
  Divider, 
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

import {
  Search,
  BookOpen,
  RefreshCw,
  Save,
  AlertTriangle
} from 'lucide-react';

import sourceService from '../CP-Sourcing/SourceService';
import synthesisService from './SynthesisService';
import ResourceList from './ResourceList';
import SynthesisCard from './SynthesisCard';
import InspirationBreadcrumb from '../CP-Inspiration/InspirationBreadCrumb';

// Styles harmonisés
const harmonizedStyles = `
  /* Styles de police harmonisés */
  .page-title {
    font-size: 1.75rem;
    line-height: 2.25rem;
    font-weight: 700;
  }
  
  .section-title {
    font-size: 1.5rem;
    line-height: 2rem;
    font-weight: 600;
  }
  
  .main-text {
    font-size: 1.125rem;
    line-height: 1.75rem;
  }
  
  .objective-text {
    font-size: 1.125rem;
    line-height: 1.75rem;
  }
  
  textarea.input-text, input.input-text {
    font-size: 1.125rem !important;
  }
  
  .button-text {
    font-size: 1rem;
  }

  /* Styles pour le fil d'ariane amélioré */
  .breadcrumb-section {
    transition: all 0.3s ease;
  }

  .breadcrumb-item {
    transition: all 0.2s ease-in-out;
    cursor: pointer;
  }

  .breadcrumb-item:hover {
    transform: translateY(-1px);
  }

  .breadcrumb-divider {
    color: #9ca3af;
    transition: color 0.2s ease;
  }

  .process-step {
    position: relative;
    display: flex;
    align-items: center;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    transition: all 0.3s ease;
    font-weight: 500;
  }

  .process-step.completed {
    background: linear-gradient(135deg, #dbeafe, #bfdbfe);
    color: #1e40af;
    box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
  }

  .process-step.active {
    background: linear-gradient(135deg, #dcfce7, #bbf7d0);
    color: #166534;
    box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
    font-weight: 600;
  }

  .process-step.pending {
    background: linear-gradient(135deg, #dbeafe, #bfdbfe);
    color: #1e40af;
  }

  .process-step:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .step-icon {
    width: 1.25rem;
    height: 1.25rem;
    margin-right: 0.5rem;
  }

  .step-connector {
    width: 2rem;
    height: 2px;
    background: linear-gradient(90deg, #e5e7eb, #d1d5db);
    margin: 0 0.5rem;
  }

  .step-connector.completed {
    background: linear-gradient(90deg, #22c55e, #16a34a);
  }
`;

export default function WebSearchSpace() {
  const { competenceId } = useParams();
  const navigate = useNavigate();
  const auth = getAuth();

  // États pour les données
  const [competence, setCompetence] = useState(null);
  const [sources, setSources] = useState([]);
  const [resources, setResources] = useState([]);
  const [synthesis, setSynthesis] = useState(null);
  
  // États pour le chargement et les erreurs
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // États pour la gestion des changements de sources
  const [sourcesChanged, setSourcesChanged] = useState(false);
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [sourceChangeInfo, setSourceChangeInfo] = useState(null);
  
  // État pour le statut du chargement
  const [loadingStatus, setLoadingStatus] = useState({
    competence: 'loading',
    synthesis: 'idle',
    resources: 'idle'
  });
 
  // Navigation vers la source manager page
  const goToSourcesPage = () => {
    navigate(`/webSource/${competenceId}`);
  };

  const handleAuthError = () => {
    navigate('/login', {
      state: {
        redirectUrl: `/competence/${competenceId}`,
        message: 'Veuillez vous connecter pour accéder à cette ressource',
      },
    });
  };

  // Chargement initial de la compétence, des sources ET de la synthèse
  useEffect(() => {
    const fetchCompetenceAndData = async () => {
      if (!competenceId) {
        setError('ID de compétence manquant');
        setLoadingStatus(prev => ({ ...prev, competence: 'error' }));
        setLoading(false);
        return;
      }

      try {
        const user = auth.currentUser;
        if (!user) {
          handleAuthError();
          return;
        }

        // 1. Récupération de la compétence
        setLoadingStatus(prev => ({ ...prev, competence: 'loading' }));
        
        const competenceDocRef = doc(db, 'users', user.uid, 'competences', competenceId);
        const competenceDocSnap = await getDoc(competenceDocRef);
        
        if (!competenceDocSnap.exists()) {
          setError('Objectif non trouvé');
          setLoadingStatus(prev => ({ ...prev, competence: 'error' }));
          setLoading(false);
          return;
        }

        const competenceData = { id: competenceId, ...competenceDocSnap.data() };
        setCompetence(competenceData);
        setLoadingStatus(prev => ({ ...prev, competence: 'success' }));

        // 2. Récupération des sources sauvegardées
        const token = await user.getIdToken(true);
        
        const result = await sourceService.getSavedSources(
          user.uid, 
          token, 
          { competenceId }
        );
        
        let formattedSources = [];
        if (result && result.success && Array.isArray(result.sources) && result.sources.length > 0) {
          // Trier pour avoir la session la plus récente
          const sortedSessions = [...result.sources].sort((a, b) => {
            const dateA = new Date(a.createdAt || a.updatedAt || 0);
            const dateB = new Date(b.createdAt || b.updatedAt || 0);
            return dateB - dateA;
          });
          
          const latestSession = sortedSessions[0];
          
          if (latestSession.sources && Array.isArray(latestSession.sources)) {
            formattedSources = latestSession.sources.map(source => ({
              ...source,
              sessionId: latestSession.id,
              title: source.title || source.domain || 'Source sans titre',
              description: source.description || 'Aucune description disponible',
              url: source.url || `https://${source.domain}` || '#',
              domain: source.domain || 'Source externe',
              type: source.type || 'webpage',
              relevanceScore: source.relevanceScore || 5
            }));
          }
        }
        
        setSources(formattedSources);

        // 3. Récupération de la synthèse existante (toujours vérifier)
        const synthesisResult = await synthesisService.getLatestSynthesis(user.uid, competenceId);
        
        if (synthesisResult.success && synthesisResult.synthesis) {
          // Transformer pour SynthesisCard
          const transformedSynthesis = {
            title: synthesisResult.synthesis.title,
            extract: synthesisResult.synthesis.content,
            url: "#",
            content_type: "synthesis",
            source: "Assistant IA",
            language: synthesisResult.synthesis.language || 'fr',
            type: "synthesis",
            datePublished: synthesisResult.synthesis.createdAt,
            relevanceScore: 100,
            id: synthesisResult.synthesis.id
          };
          
          setSynthesis(transformedSynthesis);
          
          // Formater les sources sauvegardées (de la synthèse) pour l'affichage
          let resourceResults = [];
          if (synthesisResult.synthesis.sourcesUsed && synthesisResult.synthesis.sourcesUsed.length > 0) {
            resourceResults = synthesisResult.synthesis.sourcesUsed.map(source => ({
              title: source.title || source.domain || '',
              extract: source.description || '',
              url: source.url || `https://${source.domain}` || '',
              source: source.domain || source.provider || '',
              type: source.type || 'webpage',
              language: source.language || 'fr',
              relevanceScore: source.relevanceScore || 5,
              datePublished: source.datePublished || new Date().toISOString()
            }));
          } else if (formattedSources.length > 0) {
            // Fallback sur les sources actuelles si pas de sources sauvegardées dans la synthèse
            resourceResults = formattedSources.map(source => ({
              title: source.title || source.domain || '',
              extract: source.description || '',
              url: source.url || `https://${source.domain}` || '',
              source: source.domain || source.provider || '',
              type: source.type || 'webpage',
              language: source.language || 'fr',
              relevanceScore: source.relevanceScore || 5,
              datePublished: source.datePublished || new Date().toISOString()
            }));
          }
          
          setResources(resourceResults);
          
          // Vérifier si les sources ont changé (seulement si il y a des sources actuelles)
          if (formattedSources.length > 0) {
            const changeCheck = await synthesisService.checkIfSourcesChanged(
              user.uid, 
              competenceId, 
              formattedSources
            );
            
            if (changeCheck.success && changeCheck.hasChanged) {
              setSourcesChanged(true);
              setSourceChangeInfo(changeCheck);
            }
          }
          
          setLoadingStatus({
            competence: 'success',
            synthesis: 'success',
            resources: 'success'
          });
        }

        setLoading(false);
      } catch (err) {
        console.error('Erreur:', err);
        setError(err.message || 'Erreur lors de la récupération des données');
        setLoading(false);
      }
    };

    fetchCompetenceAndData();
  }, [competenceId, auth]);

  /**
   * Générer une nouvelle synthèse
   */
  const generateSynthesis = async (isRegeneration = false) => {
    if (sources.length === 0) {
      setError('Aucune source disponible. Veuillez d\'abord sélectionner des sources dans la page d\'exploration de sources.');
      return;
    }
    
    try {
      setGenerating(true);
      setLoadingStatus({
        competence: 'success',
        synthesis: 'loading',
        resources: 'loading'
      });
      
      const user = auth.currentUser;
      if (!user) throw new Error('Utilisateur non authentifié');
      
      const token = await user.getIdToken(true);
      
      console.log("sources", sources);
      
      // Appel à l'API de synthèse
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080'}/api/search/synthesize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          query: competence?.title || '',
          sources: sources,
          userObjective: competence?.description || competence?.title || '',
          userLanguage: 'fr'
        })
      });
  
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Erreur lors de la génération de la synthèse');
      }
      
      // ✅ CORRECTION : Récupérer le query original depuis les métadonnées des sources
      // pour utiliser l'objectif de recherche court comme titre (harmonisation avec InspirationModule.jsx)
      const originalQuery = sources[0]?.query || sources[0]?.searchQuery || '';
      
      // 🔍 DEBUG - Vérifier les paramètres avant génération
      console.log('🔍 DEBUG WebSearchSpace - Paramètres avant génération synthèse:', {
        originalQuery: originalQuery,
        competenceTitle: competence?.title,
        willUseForTitle: originalQuery.trim() 
          ? originalQuery 
          : (competence?.title || "Synthèse de votre recherche")
      });
      
      // Créer le résultat de synthèse avec la même logique qu'InspirationModule.jsx
      const synthesisResult = {
        title: originalQuery.trim() 
          ? `${originalQuery}` 
          : `${competence?.title || "Synthèse de votre recherche"}`,
        searchObjective: originalQuery.trim() 
          ? `${originalQuery}` 
          : `${competence?.title || "Synthèse de votre recherche"}`,
        userObjective: competence?.title || '',
        query: originalQuery,
        extract: data.synthesis,
        url: "#",
        content_type: "synthesis",
        source: "Assistant IA",
        language: 'fr',
        type: "synthesis",
        thumbnail: null,
        datePublished: new Date().toISOString(),
        relevanceScore: 100
      };
      
      // 📦 DEBUG - Vérifier l'objet synthesisResult créé
      console.log('📦 DEBUG WebSearchSpace - Objet synthesisResult créé:', {
        title: synthesisResult.title,
        searchObjective: synthesisResult.searchObjective,
        query: synthesisResult.query,
        userObjective: synthesisResult.userObjective
      });
      
      // Sauvegarder dans Firebase
      setSaving(true);
      const saveResult = await synthesisService.saveSynthesis(
        user.uid,
        competenceId,
        synthesisResult,
        sources
      );
      
      if (saveResult.success) {
        synthesisResult.id = saveResult.synthesisId;
        console.log('Synthèse sauvegardée avec succès');
      } else {
        console.warn('Erreur lors de la sauvegarde:', saveResult.error);
      }
      
      // Formater les sources pour l'affichage comme ressources
      const resourceResults = sources.map(source => ({
        title: source.title || source.domain || '',
        extract: source.description || '',
        url: source.url || `https://${source.domain}` || '',
        source: source.domain || source.provider || '',
        type: source.type || 'webpage',
        language: source.language || 'fr',
        relevanceScore: source.relevanceScore || 5,
        datePublished: source.datePublished || new Date().toISOString()
      }));
      
      setSynthesis(synthesisResult);
      setResources(resourceResults);
      setSourcesChanged(false); // Reset le flag
      setShowRegenerateDialog(false); // Fermer le dialog
      
      setLoadingStatus({
        competence: 'success',
        synthesis: 'success',
        resources: 'success'
      });
      
    } catch (error) {
      console.error('Erreur lors de la génération de la synthèse:', error);
      setError(error.message || 'Erreur lors de la génération de la synthèse');
      
      setLoadingStatus({
        competence: 'success',
        synthesis: 'error',
        resources: 'error'
      });
    } finally {
      setGenerating(false);
      setSaving(false);
    }
  };

  // Dialog de confirmation pour la régénération
  const RegenerateDialog = () => (
    <Dialog
      open={showRegenerateDialog}
      onClose={() => setShowRegenerateDialog(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle className="flex items-center gap-2">
        <AlertTriangle className="h-5 w-5 text-orange-500" />
        Sources modifiées détectées
      </DialogTitle>
      <DialogContent>
        <DialogContentText className="mb-4">
          Les sources pour cet objectif ont été modifiées depuis la dernière synthèse.
        </DialogContentText>
        
        {sourceChangeInfo && (
          <div className="bg-orange-50 p-4 rounded-lg mb-4">
            <Typography variant="body2" className="mb-2">
              <strong>Détails des changements :</strong>
            </Typography>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Sources précédentes : {sourceChangeInfo.previousCount}</li>
              <li>• Sources actuelles : {sourceChangeInfo.currentCount}</li>
              <li>• Dernière synthèse : {new Date(sourceChangeInfo.lastSynthesisDate).toLocaleDateString('fr-FR')}</li>
            </ul>
          </div>
        )}
        
        <DialogContentText>
          Souhaitez-vous régénérer la synthèse avec les nouvelles sources ?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={() => setShowRegenerateDialog(false)}
          className="text-gray-600"
        >
          Conserver l'ancienne
        </Button>
        <Button 
          onClick={() => generateSynthesis(true)}
          variant="contained"
          className="bg-orange-500 hover:bg-orange-600"
          disabled={generating}
        >
          {generating ? <CircularProgress size={20} className="mr-2" /> : null}
          Régénérer
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Affichage pendant le chargement
  if (loading) {
    return (
      <div className="w-full min-h-screen bg-gradient-to-b from-blue-50 via-indigo-50 to-white">
        <style>{harmonizedStyles}</style>
        <div className="flex justify-center items-center h-64">
          <CircularProgress className="text-blue-600" />
          <Typography variant="body1" className="ml-2 text-blue-700">
            Chargement de votre espace d'analyse...
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-gradient-to-b from-blue-50 via-indigo-50 to-white">
      {/* Injecter les styles harmonisés */}
      <style>{harmonizedStyles}</style>

      {/* Dialog de régénération */}
      <RegenerateDialog />

      <div className="max-w-6xl mx-auto px-4 py-8">

        <InspirationBreadcrumb 
          competenceId={competenceId}
          currentPage="synthesis"
        />

        {/* Affichage de l'objectif */}
        {competence && (
          <div className="w-full bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-lg border border-gray-200 p-6 mb-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
              <div className="w-full md:pr-4">
                <div className="flex items-baseline">
                  <h2 className="text-2xl font-semibold text-gray-800 whitespace-nowrap mr-2">
                    Mon objectif :
                  </h2>
                  <span className="text-lg text-gray-700 font-medium">
                    {competence?.title || "Chargement de l'objectif..."}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Affichage des erreurs */}
        {error && (
          <Alert severity="error" className="mb-6 rounded-lg shadow-sm">
            {error}
          </Alert>
        )}

        {/* Module d'affichage des sources */}
        <div className="w-full bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl shadow-lg border border-gray-200 p-6 mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
            <Search className="w-5 h-5 mr-2 text-purple-600" />
            Sources sauvegardées pour cet objectif
          </h3>
          
          <div className="bg-white p-6 rounded-lg border border-gray-100">
            {sources.length > 0 ? (
              <>
                <Typography variant="body2" color="text.secondary" className="mb-4">
                  {sources.length} source(s) disponible(s) pour générer la synthèse
                </Typography>
                
                <Divider className="mb-4" />
                
                <div className="flex flex-wrap gap-2 mb-4">
                  {sources.map((source, index) => (
                    <Chip
                      key={index}
                      label={source.title || source.domain}
                      variant="outlined"
                      className="border-blue-300 text-blue-700 hover:bg-blue-50"
                      onClick={() => window.open(source.url || `https://${source.domain}`, '_blank')}
                    />
                  ))}
                </div>
                
                <div className="flex gap-3">
                  <Button
                    variant="contained"
                    className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors font-medium"
                    onClick={() => generateSynthesis(false)}
                    disabled={generating || saving}
                  >
                    {generating ? (
                      <>
                        <CircularProgress size={20} className="mr-2 text-purple-700" /> 
                        Génération en cours...
                      </>
                    ) : saving ? (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Sauvegarde...
                      </>
                    ) : synthesis ? (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Régénérer la synthèse
                      </>
                    ) : (
                      'Générer la synthèse'
                    )}
                  </Button>

                  <Button
                    variant="outlined"
                    className="px-4 py-2 border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors font-medium"
                    onClick={goToSourcesPage}
                  >
                    {synthesis ? 'Modifier les sources' : 'Ajouter des sources'}
                  </Button>
                </div>
              </>
            ) : (
              <>
                {synthesis ? (
                  // Cas où il y a une synthèse mais plus de sources actuelles
                  <>
                    <Alert severity="warning" className="mb-4 rounded-lg">
                      Une synthèse existe pour cet objectif, mais aucune source n'est actuellement sauvegardée.
                    </Alert>
                    
                    <Typography variant="body2" color="text.secondary" className="mb-4">
                      Vous pouvez consulter votre synthèse existante ci-dessous, ou ajouter de nouvelles sources 
                      pour créer une synthèse mise à jour.
                    </Typography>
                    
                    <Button
                      variant="contained"
                      className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors font-medium"
                      onClick={goToSourcesPage}
                    >
                      Ajouter de nouvelles sources
                    </Button>
                  </>
                ) : (
                  // Cas où il n'y a ni synthèse ni sources
                  <>
                    <Alert severity="info" className="mb-4 rounded-lg">
                      Aucune source n'a été sauvegardée pour cet objectif.
                    </Alert>
                    
                    <Typography variant="body2" color="text.secondary" className="mb-4">
                      Pour générer une synthèse, vous devez d'abord sélectionner et sauvegarder des sources.
                    </Typography>
                    
                    <Button
                      variant="contained"
                      className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors font-medium"
                      onClick={goToSourcesPage}
                    >
                      Explorer et sélectionner des sources
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Affichage de la synthèse si disponible */}
        {loadingStatus.synthesis === 'loading' ? (
          <div className="flex justify-center items-center p-8 bg-white rounded-xl shadow-md mb-8">
            <CircularProgress className="text-blue-600" />
            <Typography variant="body1" className="ml-2 text-blue-700">
              {saving ? 'Génération et sauvegarde en cours...' : 'Génération de la synthèse en cours...'}
            </Typography>
          </div>
        ) : synthesis && (
          <div className="mb-8">
            <SynthesisCard synthesis={synthesis} />
          </div>
        )}

      {/* Affichage des ressources si disponibles */}
      {loadingStatus.resources === 'loading' ? (
        <div className="flex justify-center p-6">
          <CircularProgress className="text-blue-600" />
        </div>
      ) : resources.length > 0 && (
        <div className="mt-8 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl shadow-lg border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <BookOpen className="w-5 h-5 text-purple-700" />
            <h3 className="text-xl font-semibold text-gray-800">
              Sources utilisées pour la synthèse
            </h3>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-lg border border-gray-100">
            <ResourceList resources={resources} />
          </div>
        </div>
      )}

      </div>
    </div>
  );
}
